import { useEffect, useState } from 'react';
import { fetchToolDetail, fetchUserDetail, fetchQuestionDetail } from '../services/dashboard.service';
import { useFilterContext } from '../usageandbilling/FilterContext';

export type AgentUsageDetailKind = 'tool' | 'user' | 'question';

export interface ToolDetail {
  tool: string;
  calls: number;
  totalTokens: number;
  totalLatencyMs: number;
  usersCount: number;
  questions: Array<{ question: string; count: number }>;
  topUsers: Array<{ userId: string; userName: string; calls: number; totalTokens: number }>;
}

export interface UserDetail {
  userId: string;
  calls: number;
  totalTokens: number;
  totalLatencyMs: number;
  sessionsCount: number;
  byTool: Array<{ tool: string; calls: number; totalTokens: number }>;
  topQuestions: Array<{ question: string; count: number }>;
}

export interface QuestionDetail {
  question: string;
  count: number;
  usersCount: number;
  calls: number;
  totalTokens: number;
  totalLatencyMs: number;
  byTool: Array<{ tool: string; calls: number; totalTokens: number }>;
  topUsers: Array<{ userId: string; userName: string; calls: number; totalTokens: number }>;
}

export type AgentUsageDetail = ToolDetail | UserDetail | QuestionDetail;

/** Fetches drill-down detail for one Top 5 bar — which endpoint depends on `kind`. Only fetches while `open` and `identifier` are set. */
export function useAgentUsageDetail(kind: AgentUsageDetailKind | null, identifier: string | null, open: boolean) {
  const { selectedOrgIds, selectedUserIds, dateRange } = useFilterContext();
  const [data, setData] = useState<AgentUsageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !kind || !identifier || !dateRange[0] || !dateRange[1]) return;
    const controller = new AbortController();

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const params = new URLSearchParams({
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        });
        if (selectedOrgIds.length > 0) params.set('orgIds', selectedOrgIds.join(','));
        if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));

        let response: AgentUsageDetail;
        if (kind === 'tool') {
          params.set('tool', identifier);
          response = await fetchToolDetail(params.toString(), controller.signal) as ToolDetail;
        } else if (kind === 'user') {
          params.set('userId', identifier);
          response = await fetchUserDetail(params.toString(), controller.signal) as UserDetail;
        } else {
          params.set('normalizedMessage', identifier);
          response = await fetchQuestionDetail(params.toString(), controller.signal) as QuestionDetail;
        }
        setData(response);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError('Failed to load details.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
    return () => controller.abort();
  }, [kind, identifier, open, selectedOrgIds, selectedUserIds, dateRange]);

  return { data, loading, error };
}
